import React, { useState } from "react";
import { links } from "./links";
// mui
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
} from "@mui/material";
import { Link } from "react-router-dom";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { LuDot } from "react-icons/lu";
import { usePermissionChecker } from "../../hooks/UsePermissionsChecker";

const LinksDrawer = ({
  showLinkIconsOnly = false,
}: {
  showLinkIconsOnly?: boolean;
}) => {
  return (
    <div>
      <List>
        {links.map((link, index) => {
          // When in icon-only mode, if the link has children, render children directly
          if (showLinkIconsOnly && link.children) {
            return (
              <React.Fragment key={link.path + index}>
                {link.children.map((childLink: any, childIndex: number) => (
                  <SidebarLink
                    key={childLink.path + childIndex}
                    link={childLink}
                    showLinkIconsOnly={showLinkIconsOnly}
                  />
                ))}
              </React.Fragment>
            );
          }

          // Otherwise render the normal link
          return (
            <SidebarLink
              key={link.path + index}
              link={link}
              showLinkIconsOnly={showLinkIconsOnly}
            />
          );
        })}
      </List>
    </div>
  );
};

export default LinksDrawer;

const SidebarLink = ({
  link,
  showLinkIconsOnly,
}: {
  link: any;
  showLinkIconsOnly: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const { hasPermission, isDataReady } = usePermissionChecker();

  // Check if user has permission to view this link
  const isAllowed = isDataReady
    ? hasPermission(link.permissions, false)
    : false;

  const handleClick = () => {
    if (link.children) {
      setOpen(!open);
    }
  };

  // Don't render if user doesn't have permission
  if (!isAllowed) {
    return null;
  }

  // Don't render parent links with children when in icon-only mode
  if (showLinkIconsOnly && link.children) {
    return null;
  }

  // Filter children based on permissions
  const allowedChildren = link.children?.filter((child: any) =>
    hasPermission(child.permissions, false)
  );

  // If parent has no allowed children, don't show the parent either
  if (link.children && allowedChildren?.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={link?.title}
        placement={"left"}
        hidden={!showLinkIconsOnly}
        key={link.path}
      >
        <ListItem
          disablePadding
          sx={{
            color: "white",
          }}
        >
          {link.children ? (
            // Render button for links with children
            <ListItemButton onClick={handleClick}>
              <ListItemIcon
                sx={{
                  fontSize: "22px",
                  color: "inherit",
                  m: "auto",
                  minWidth: showLinkIconsOnly ? "22px" : "32px",
                }}
              >
                {<link.icon />}
              </ListItemIcon>
              {!showLinkIconsOnly && <ListItemText primary={link.title} />}
              {!showLinkIconsOnly &&
                (open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />)}
            </ListItemButton>
          ) : (
            // Render normal link for links without children
            <ListItemButton component={Link} to={link.path}>
              <ListItemIcon
                sx={{
                  fontSize: "22px",
                  color: "inherit",
                  m: "auto",
                  minWidth: showLinkIconsOnly ? "22px" : "32px",
                }}
              >
                {<link.icon />}
              </ListItemIcon>
              {!showLinkIconsOnly && <ListItemText primary={link.title} />}
            </ListItemButton>
          )}
        </ListItem>
      </Tooltip>

      {/* Collapsible section for child links only shown when not in icon-only mode */}
      {link.children &&
        !showLinkIconsOnly &&
        allowedChildren &&
        allowedChildren.length > 0 && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List
              component="div"
              disablePadding
              sx={{ paddingInlineStart: "8px" }}
            >
              {allowedChildren.map((childLink: any, index: number) => (
                <SidebarLink
                  key={childLink.path + index}
                  link={{
                    ...childLink,
                    icon: !showLinkIconsOnly ? LuDot : childLink.icon,
                  }}
                  showLinkIconsOnly={showLinkIconsOnly}
                />
              ))}
            </List>
          </Collapse>
        )}
    </>
  );
};
